import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  useQueries,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  AlertTriangle,
  CalendarDays,
  CalendarPlus,
  CheckCircle2,
  ChevronRight,
  CloudOff,
  FileEdit,
  FileSignature,
  FileText,
  FileX2,
  Inbox,
  Loader2,
  Radio,
  RefreshCw,
  Upload,
  UserCog,
} from "lucide-react";
import { authBridge } from "@/lib/auth/bridge";
import { useToastMutation } from "@/lib/mutations";
import { Button } from "@/components/ui/button";
import { apiFetch, ApiError } from "@/api/fetch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { PageContainer } from "@/components/layout/WorkspaceShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { useOrg } from "@/lib/org/OrgContext";
import { companiesQueries, type CompanyDto } from "@/lib/companies/api";
import {
  type DriveConnectionStatusDto,
} from "@/lib/drive/api";
import {
  classifyEmployee,
  employeesQueries,
  type EmployeeDirectoryRowDto,
} from "@/lib/employees/api";
import { ContractStatus } from "@/lib/contracts/api";
import { calendarQueries, type CalendarEventDto } from "@/lib/calendar/api";
import { startOfWeek } from "@/lib/calendar/week";
import { formatEventDay, formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

// ── Wire types not yet in shared helpers ──────────────────────────────────

interface MyCalendarItemDto {
  kind: "javeed-event" | "ordio-shift" | "ordio-absence";
  startsAt: string;
  endsAt: string;
  title: string;
  eventId: string | null;
  ordioIdentityId: string | null;
  companyId: string | null;
}

interface NoteSummaryDto {
  id: string;
  ownerId: string;
  title: string;
  preview: string;
  updatedAt: string;
}

interface DocumentSummaryDto {
  id: string;
  fileName: string;
  status: string;
  category: string | null;
  classificationConfidence: number | null;
  needsReview: boolean;
  source: string;
  createdAt: string;
  processedAt: string | null;
  driveFileId: string | null;
}

interface ContractSummaryDto {
  id: string;
  status: number; // ContractStatus enum on the wire
  contractType: string;
  employeeDisplayName: string;
  companyName: string;
  createdAt: string;
  employmentId: string | null;
  kind: "NewContract" | "Amendment" | "Adoption";
}

interface TimeClockEntryDto {
  companyId: string;
  companyName: string;
  ordioEmployeeId: string | null;
  employeeDisplayName: string;
  shiftStartsAt: string;
  shiftEndsAt: string;
  clockedInAt: string | null;
  clockedOutAt: string | null;
  branchName: string | null;
  workingAreaName: string | null;
}

interface TimeClockResponseDto {
  generatedAt: string;
  windowFrom: string;
  live: TimeClockEntryDto[];
  completed: TimeClockEntryDto[];
  unstamped: TimeClockEntryDto[];
  failedCompanyNames: string[];
}

interface SyncStatusDto {
  generatedAt: string;
  companies: CompanySyncStatusDto[];
}

interface CompanySyncStatusDto {
  companyId: string;
  companyName: string;
  ordioId: number;
  isStale: boolean;
  lastSeenAt: string | null;
  lastBaseDataSyncedAt: string | null;
  lastBranchesSyncedAt: string | null;
  lastShiftsFullRefetchedAt: string | null;
}

export const Route = createFileRoute("/o/$slug/")({
  component: DashboardPage,
});

// ── Page ──────────────────────────────────────────────────────────────────

function DashboardPage() {
  const org = useOrg();
  const slug = org.slug;
  const window = useDashboardWindow();

  // Org-scoped company list drives every fan-out below. Using the dedicated
  // /api/o/{slug}/companies endpoint instead of /api/companies prevents
  // cross-org leakage for multi-org users.
  const companies = useQuery(companiesQueries.forOrg(slug));

  // All org events visible to the user — same endpoint the week-view uses, so
  // the dashboard agenda mirrors what the user sees on /o/{slug}/calendar
  // rather than only events they're personally assigned to.
  const orgEvents = useQuery(calendarQueries.events(slug, window.fromIso, window.toIso));
  // Personal stream is still useful for Ordio shifts + absences (which the
  // events endpoint doesn't include). We filter out its `javeed-event` rows
  // below to avoid duplicating events the user is also an attendee of.
  const personal = useQuery({
    queryKey: ["calendar", "me", slug, window.fromIso, window.toIso],
    queryFn: () =>
      apiFetch<MyCalendarItemDto[]>(
        `/api/o/${slug}/calendar/me/events?from=${encodeURIComponent(
          window.fromIso,
        )}&to=${encodeURIComponent(window.toIso)}`,
      ),
  });
  const events = {
    data: useMemo(
      () => mergeAgendaSources(orgEvents.data, personal.data),
      [orgEvents.data, personal.data],
    ),
    isLoading: orgEvents.isLoading || personal.isLoading,
    error: (orgEvents.error ?? personal.error) as Error | null,
  };

  const notes = useQuery({
    queryKey: ["notes", slug],
    queryFn: () => apiFetch<NoteSummaryDto[]>(`/api/o/${slug}/notes`),
    staleTime: 30_000,
  });

  // Admin-only — gracefully hides on 403.
  const sync = useQuery<SyncStatusDto, Error>({
    queryKey: ["sync-status", slug],
    queryFn: () => apiFetch<SyncStatusDto>(`/api/o/${slug}/sync-status`),
    retry: false,
    staleTime: 60_000,
  });

  const signals = useDashboardSignals(slug, companies.data ?? []);
  const live = useLiveEntries(slug, companies.data ?? []);

  const { today, week } = useMemo(() => splitAgenda(events.data ?? []), [events.data]);

  return (
    <PageContainer>
      <PageHeader title={org.name} description="Was heute zählt." />

      <ActionStrip signals={signals} slug={slug} loading={companies.isLoading} />

      <QuickActions slug={slug} companies={companies.data ?? []} />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <AgendaCard
            today={today}
            isLoading={events.isLoading}
            error={events.error}
            slug={slug}
            live={live}
          />
          <WeekCard items={week} slug={slug} />
        </div>

        <div className="space-y-4">
          <NotesCard query={notes} slug={slug} />
          <HealthCard
            slug={slug}
            sync={sync}
            driveDisconnected={signals.driveDisconnected}
            companiesLoading={companies.isLoading}
          />
        </div>
      </div>
    </PageContainer>
  );
}

// ── Quick actions ─────────────────────────────────────────────────────────

function QuickActions({
  slug,
  companies,
}: {
  slug: string;
  companies: CompanyDto[];
}) {
  const [uploadOpen, setUploadOpen] = useState(false);

  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-2 lg:grid-cols-4">
        <QuickActionTile
          icon={<FileSignature className="h-4 w-4" />}
          label="Arbeitsvertrag erstellen"
          to="/o/$slug/contracts/new"
          slug={slug}
        />
        <QuickActionTile
          icon={<FileEdit className="h-4 w-4" />}
          label="Vertragsergänzung erstellen"
          to="/o/$slug/employees"
          slug={slug}
        />
        <QuickActionTile
          icon={<Upload className="h-4 w-4" />}
          label="Dokument hochladen"
          onClick={() => setUploadOpen(true)}
          disabled={companies.length === 0}
        />
        <QuickActionTile
          icon={<CalendarPlus className="h-4 w-4" />}
          label="Neuer Termin"
          to="/o/$slug/calendar"
          slug={slug}
          search={{ create: "1" }}
        />
      </div>

      <DocumentUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        companies={companies}
      />
    </>
  );
}

type QuickActionTileProps = {
  icon: React.ReactNode;
  label: string;
} & (
  | {
      to: string;
      slug: string;
      search?: Record<string, string>;
      onClick?: undefined;
      disabled?: undefined;
    }
  | {
      onClick: () => void;
      disabled?: boolean;
      to?: undefined;
      slug?: undefined;
      search?: undefined;
    }
);

function QuickActionTile(props: QuickActionTileProps) {
  const inner = (
    <span className="flex h-full w-full items-center gap-2 rounded-md border bg-card px-3 py-2.5 text-sm text-foreground shadow-sm transition-colors hover:bg-muted">
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-primary/10 text-primary">
        {props.icon}
      </span>
      <span className="min-w-0 flex-1 font-medium leading-tight">{props.label}</span>
    </span>
  );

  if ("onClick" in props && props.onClick) {
    return (
      <button
        type="button"
        onClick={props.onClick}
        disabled={props.disabled}
        className="text-left disabled:cursor-not-allowed disabled:opacity-50"
      >
        {inner}
      </button>
    );
  }

  return (
    <Link
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      to={props.to as any}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      params={{ slug: props.slug } as any}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      search={props.search as any}
      className="no-underline"
    >
      {inner}
    </Link>
  );
}

function DocumentUploadDialog({
  open,
  onOpenChange,
  companies,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companies: CompanyDto[];
}) {
  const [companyId, setCompanyId] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);

  // Pick the first company by default once the list arrives so the user can
  // just attach a file and submit when they only have one.
  useEffect(() => {
    if (!companyId && companies.length > 0) setCompanyId(companies[0].id);
  }, [companyId, companies]);

  const upload = useToastMutation({
    mutationFn: async (vars: { companyId: string; file: File }) => {
      const token = authBridge.getAccessToken();
      const form = new FormData();
      form.append("file", vars.file, vars.file.name);
      const res = await fetch(`/api/companies/${vars.companyId}/documents`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: form,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Upload fehlgeschlagen (${res.status}).`);
      }
    },
    toasts: {
      loading: "Wird hochgeladen …",
      success: "Dokument hochgeladen. Verarbeitung läuft.",
    },
    onSuccess: () => {
      setFile(null);
      onOpenChange(false);
    },
  });

  const canSubmit = !!companyId && !!file && !upload.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setFile(null);
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Dokument hochladen</DialogTitle>
          <DialogDescription>
            Datei wird in die Verarbeitungs-Pipeline aufgenommen (OCR,
            Klassifikation, Drive-Ablage).
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSubmit) return;
            upload.mutate({ companyId, file: file! });
          }}
          className="space-y-3"
        >
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Unternehmen
            </label>
            <select
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              disabled={companies.length <= 1 || upload.isPending}
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
            >
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Datei (max. 50&nbsp;MB)
            </label>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              disabled={upload.isPending}
              className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={upload.isPending}
            >
              Abbrechen
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {upload.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              Hochladen
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Action strip ──────────────────────────────────────────────────────────

interface DashboardSignals {
  documentsToReview: { count: number; firstCompanyId: string | null };
  contractsSendFailed: { count: number; firstCompanyId: string | null };
  contractDrafts: { count: number; firstCompanyId: string | null };
  orphanEmployees: { count: number; firstCompanyId: string | null };
  driveDisconnected: { count: number; firstCompanyId: string | null };
  isLoading: boolean;
  hasAnyError: boolean;
}

function ActionStrip({
  signals,
  slug,
  loading,
}: {
  signals: DashboardSignals;
  slug: string;
  loading: boolean;
}) {
  if (loading || signals.isLoading) {
    return (
      <div className="mb-4 flex flex-wrap gap-2">
        <Skeleton className="h-10 w-44" />
        <Skeleton className="h-10 w-44" />
        <Skeleton className="h-10 w-44" />
      </div>
    );
  }

  const chips: ActionChipProps[] = [];

  if (signals.documentsToReview.count > 0 && signals.documentsToReview.firstCompanyId) {
    chips.push({
      icon: <Inbox className="h-4 w-4" />,
      label: "Belege zur Prüfung",
      count: signals.documentsToReview.count,
      tone: "amber",
      // No dedicated review-queue route yet — link to the documents list of
      // the first company that has pending items. Multi-company drill-down
      // is a follow-up once a dedicated org-level review queue lands.
      href: `/api/companies/${signals.documentsToReview.firstCompanyId}/documents`,
      external: true,
    });
  }
  if (signals.contractsSendFailed.count > 0) {
    chips.push({
      icon: <FileX2 className="h-4 w-4" />,
      label: "Verträge mit Versandfehler",
      count: signals.contractsSendFailed.count,
      tone: "red",
      to: "/o/$slug/contracts",
      slug,
    });
  }
  if (signals.contractDrafts.count > 0) {
    chips.push({
      icon: <FileSignature className="h-4 w-4" />,
      label: "Vertragsentwürfe",
      count: signals.contractDrafts.count,
      tone: "muted",
      to: "/o/$slug/contracts",
      slug,
    });
  }
  if (signals.orphanEmployees.count > 0 && signals.orphanEmployees.firstCompanyId) {
    chips.push({
      icon: <UserCog className="h-4 w-4" />,
      label: "Personen ohne AV",
      count: signals.orphanEmployees.count,
      tone: "amber",
      to: "/o/$slug/employees",
      slug,
      search: { companyId: signals.orphanEmployees.firstCompanyId, status: "orphan-wages" as const },
    });
  }
  if (signals.driveDisconnected.count > 0) {
    chips.push({
      icon: <CloudOff className="h-4 w-4" />,
      label: "Drive nicht verbunden",
      count: signals.driveDisconnected.count,
      tone: "muted",
      to: "/o/$slug/drive",
      slug,
    });
  }

  if (chips.length === 0) {
    return (
      <div className="mb-4 inline-flex items-center gap-2 rounded-md border bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
        <CheckCircle2 className="h-4 w-4" />
        Keine offenen Aufgaben.
      </div>
    );
  }

  return (
    <div className="mb-4 flex flex-wrap gap-2">
      {chips.map((c, i) => (
        <ActionChip key={i} {...c} />
      ))}
    </div>
  );
}

type ActionChipTone = "amber" | "red" | "muted";

type ActionChipProps = {
  icon: React.ReactNode;
  label: string;
  count: number;
  tone: ActionChipTone;
} & (
  | { to: string; slug: string; search?: Record<string, string>; href?: undefined; external?: undefined }
  | { href: string; external: true; to?: undefined; slug?: undefined; search?: undefined }
);

function ActionChip(props: ActionChipProps) {
  const toneClass = {
    amber: "border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100",
    red: "border-red-200 bg-red-50 text-red-900 hover:bg-red-100",
    muted: "border-border bg-card text-foreground hover:bg-muted",
  }[props.tone];

  const body = (
    <span className={cn("inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors", toneClass)}>
      {props.icon}
      <span className="font-medium tabular-nums">{props.count}</span>
      <span>{props.label}</span>
      <ChevronRight className="h-3.5 w-3.5 opacity-60" />
    </span>
  );

  if ("href" in props && props.href) {
    return (
      <a href={props.href} className="no-underline">
        {body}
      </a>
    );
  }

  // The TanStack Link `to` is a templated path; the type checker reads `to`
  // as a literal so passing an arbitrary string from props would need a cast.
  // For the routes we actually use this is fine — they're all known string
  // literals constructed at the call sites above.
  return (
    <Link
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      to={props.to as any}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      params={{ slug: props.slug } as any}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      search={props.search as any}
      className="no-underline"
    >
      {body}
    </Link>
  );
}

// ── Today / Week ──────────────────────────────────────────────────────────

function AgendaCard({
  today,
  isLoading,
  error,
  slug,
  live,
}: {
  today: MyCalendarItemDto[];
  isLoading: boolean;
  error: Error | null;
  slug: string;
  live: LiveEntries;
}) {
  const [selected, setSelected] = useState<TimeClockEntryDto | null>(null);
  const showMultiCompany = live.companyCount > 1;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            Heute
          </CardTitle>
          <Link
            to="/o/$slug/calendar"
            params={{ slug }}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Kalender öffnen
          </Link>
        </div>
        <CardDescription>Termine, Schichten und Abwesenheiten für den heutigen Tag</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <TodayBody items={today} isLoading={isLoading} error={error} />
          </div>
          <LiveSidePanel
            live={live}
            showCompany={showMultiCompany}
            onSelect={setSelected}
          />
        </div>
      </CardContent>
      <EmployeeContactDialog
        slug={slug}
        entry={selected}
        onClose={() => setSelected(null)}
      />
    </Card>
  );
}

function TodayBody({
  items,
  isLoading,
  error,
}: {
  items: MyCalendarItemDto[];
  isLoading: boolean;
  error: Error | null;
}) {
  if (isLoading) return <ListSkeleton rows={3} />;
  if (error) return <InlineError error={error} />;
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">Nichts geplant für heute.</p>;
  }
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <AgendaRow
          key={`${item.eventId ?? item.ordioIdentityId ?? "x"}-${item.startsAt}-${i}`}
          item={item}
          prominent
        />
      ))}
    </ul>
  );
}

function LiveSidePanel({
  live,
  showCompany,
  onSelect,
}: {
  live: LiveEntries;
  showCompany: boolean;
  onSelect: (entry: TimeClockEntryDto) => void;
}) {
  return (
    <aside
      aria-label="Live"
      className="rounded-md border bg-muted/30 p-3"
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Radio
            className={cn(
              "h-3.5 w-3.5",
              live.entries.length > 0 && "text-emerald-600",
            )}
          />
          <span>Live</span>
          {live.entries.length > 0 && (
            <span className="ml-0.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-emerald-700">
              {live.entries.length}
            </span>
          )}
        </div>
        {live.isFetching && (
          <span className="text-[10px] text-muted-foreground">…</span>
        )}
      </div>
      {live.isLoading ? (
        <div className="space-y-1.5">
          <Skeleton className="h-6" />
          <Skeleton className="h-6" />
        </div>
      ) : live.entries.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {live.hasErrors ? "Keine Daten." : "Niemand eingestempelt."}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {live.entries.slice(0, 6).map((e, i) => (
            <LiveRow
              key={`${e.companyId}-${e.ordioEmployeeId ?? e.employeeDisplayName}-${e.clockedInAt ?? i}`}
              entry={e}
              showCompany={showCompany}
              onSelect={onSelect}
            />
          ))}
          {live.entries.length > 6 && (
            <li className="pt-1 text-center text-[11px] text-muted-foreground">
              +{live.entries.length - 6} weitere
            </li>
          )}
        </ul>
      )}
    </aside>
  );
}

function LiveRow({
  entry,
  showCompany,
  onSelect,
}: {
  entry: TimeClockEntryDto;
  showCompany: boolean;
  onSelect: (entry: TimeClockEntryDto) => void;
}) {
  const since = formatSince(entry.clockedInAt ?? entry.shiftStartsAt);
  const where = [entry.branchName, entry.workingAreaName].filter(Boolean).join(" · ");
  const sub = [showCompany ? entry.companyName : null, where || null].filter(Boolean).join(" · ");
  const clickable = !!entry.ordioEmployeeId;
  return (
    <li className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        {clickable ? (
          <button
            type="button"
            onClick={() => onSelect(entry)}
            className="block w-full truncate text-left text-xs font-medium hover:underline focus:outline-none focus:underline"
          >
            {entry.employeeDisplayName}
          </button>
        ) : (
          <div className="truncate text-xs font-medium">{entry.employeeDisplayName}</div>
        )}
        {sub && (
          <div className="truncate text-[11px] text-muted-foreground">{sub}</div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <span className="relative inline-flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
        </span>
        <span className="text-[11px] tabular-nums text-emerald-700">{since}</span>
      </div>
    </li>
  );
}

function EmployeeContactDialog({
  slug,
  entry,
  onClose,
}: {
  slug: string;
  entry: TimeClockEntryDto | null;
  onClose: () => void;
}) {
  // The directory endpoint already powers useDashboardSignals, so this query
  // is normally a cache hit. enabled-gated so we don't fetch until the dialog
  // is opened.
  const directory = useQuery({
    ...employeesQueries.directory(slug, entry?.companyId ?? ""),
    enabled: !!entry,
    staleTime: 5 * 60_000,
  });

  const row = useMemo(() => {
    if (!entry || !directory.data) return null;
    return directory.data.find((r) => r.ordioEmployeeId === entry.ordioEmployeeId) ?? null;
  }, [entry, directory.data]);

  return (
    <Dialog open={!!entry} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{entry?.employeeDisplayName ?? "—"}</DialogTitle>
          <DialogDescription>
            {entry?.companyName}
            {entry?.branchName ? ` · ${entry.branchName}` : ""}
          </DialogDescription>
        </DialogHeader>
        {directory.isLoading && !directory.data ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : directory.error ? (
          <InlineError error={directory.error} />
        ) : !row ? (
          <p className="text-sm text-muted-foreground">
            Stammdaten nicht verfügbar.
          </p>
        ) : (
          <dl className="space-y-2 text-sm">
            <ContactField label="Name" value={row.employeeDisplayName} />
            <ContactField
              label="E-Mail"
              value={row.email}
              href={row.email ? `mailto:${row.email}` : null}
            />
            <ContactField
              label="Telefon"
              value={row.phone}
              href={row.phone ? `tel:${row.phone}` : null}
            />
          </dl>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ContactField({
  label,
  value,
  href,
}: {
  label: string;
  value: string | null;
  href?: string | null;
}) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="w-20 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="min-w-0 flex-1 truncate">
        {value ? (
          href ? (
            <a href={href} className="hover:underline">
              {value}
            </a>
          ) : (
            value
          )
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </dd>
    </div>
  );
}

function formatSince(input: string | null): string {
  if (!input) return "—";
  const startedMs = new Date(input).getTime();
  if (Number.isNaN(startedMs)) return "—";
  const diffMin = Math.max(0, Math.round((Date.now() - startedMs) / 60_000));
  if (diffMin < 60) return `seit ${diffMin} Min`;
  const h = Math.floor(diffMin / 60);
  const m = diffMin % 60;
  return m === 0 ? `seit ${h} Std` : `seit ${h} Std ${m} Min`;
}

function WeekCard({ items, slug }: { items: MyCalendarItemDto[]; slug: string }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Diese Woche</CardTitle>
          <Link
            to="/o/$slug/calendar"
            params={{ slug }}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Alle anzeigen
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Keine weiteren Einträge bis Wochenende.</p>
        ) : (
          <ul className="divide-y">
            {items.slice(0, 6).map((item, i) => (
              <AgendaRow
                key={`${item.eventId ?? item.ordioIdentityId ?? "x"}-${item.startsAt}-${i}`}
                item={item}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function AgendaRow({ item, prominent }: { item: MyCalendarItemDto; prominent?: boolean }) {
  return (
    <li
      className={cn(
        "flex items-start justify-between gap-3 py-2",
        prominent && "rounded-md border bg-background p-3",
      )}
    >
      <div className="min-w-0">
        <div className={cn("truncate text-sm", prominent ? "font-medium" : "")}>{item.title}</div>
        <div className="text-xs text-muted-foreground">{formatEventDay(item.startsAt)}</div>
      </div>
      <KindBadge kind={item.kind} />
    </li>
  );
}

function KindBadge({ kind }: { kind: MyCalendarItemDto["kind"] }) {
  const map: Record<MyCalendarItemDto["kind"], { label: string; cls: string }> = {
    "javeed-event": { label: "Termin", cls: "bg-blue-100 text-blue-700" },
    "ordio-shift": { label: "Schicht", cls: "bg-emerald-100 text-emerald-700" },
    "ordio-absence": { label: "Abwesend", cls: "bg-amber-100 text-amber-700" },
  };
  const { label, cls } = map[kind];
  return <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${cls}`}>{label}</span>;
}

// ── Notes ─────────────────────────────────────────────────────────────────

function NotesCard({
  query,
  slug,
}: {
  query: UseQueryResult<NoteSummaryDto[], Error>;
  slug: string;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-4 w-4 text-muted-foreground" />
            Notizen
          </CardTitle>
          <Link
            to="/o/$slug/notes"
            params={{ slug }}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Alle
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {query.isLoading && <ListSkeleton rows={2} />}
        {query.error && <InlineError error={query.error} />}
        {query.data && query.data.length === 0 && (
          <p className="text-sm text-muted-foreground">Noch keine Notizen.</p>
        )}
        {query.data && query.data.length > 0 && (
          <ul className="space-y-2">
            {[...query.data]
              .sort((a, b) => +new Date(b.updatedAt) - +new Date(a.updatedAt))
              .slice(0, 4)
              .map((n) => (
                <li key={n.id}>
                  <Link
                    to="/o/$slug/notes/$noteId"
                    params={{ slug, noteId: n.id }}
                    className="block rounded-md border bg-background p-2.5 hover:bg-muted"
                  >
                    <div className="truncate text-sm font-medium">{n.title || "Ohne Titel"}</div>
                    {n.preview && (
                      <div className="line-clamp-1 text-xs text-muted-foreground">{n.preview}</div>
                    )}
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      {formatRelative(n.updatedAt) ?? "—"}
                    </div>
                  </Link>
                </li>
              ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ── Health ────────────────────────────────────────────────────────────────

function HealthCard({
  slug,
  sync,
  driveDisconnected,
  companiesLoading,
}: {
  slug: string;
  sync: UseQueryResult<SyncStatusDto, Error>;
  driveDisconnected: DashboardSignals["driveDisconnected"];
  companiesLoading: boolean;
}) {
  // 403 = not org admin. Hide silently — these signals are admin-only and the
  // dashboard shouldn't nag non-admins about them.
  const isAdminGated = sync.error instanceof ApiError && sync.error.status === 403;
  const stale = sync.data?.companies.filter((c) => c.isStale) ?? [];

  const showAdminBlock = !isAdminGated;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <RefreshCw className="h-4 w-4 text-muted-foreground" />
          System
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!companiesLoading && driveDisconnected.count > 0 && (
          <div className="flex items-start gap-2 rounded-md border bg-amber-50 p-2.5 text-sm text-amber-900">
            <CloudOff className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="min-w-0 flex-1">
              <div>
                {driveDisconnected.count === 1
                  ? "1 Unternehmen ohne Google-Drive."
                  : `${driveDisconnected.count} Unternehmen ohne Google-Drive.`}
              </div>
              <Link
                to="/o/$slug/drive"
                params={{ slug }}
                className="text-xs underline-offset-2 hover:underline"
              >
                Drive verbinden
              </Link>
            </div>
          </div>
        )}

        {showAdminBlock && sync.isLoading && <ListSkeleton rows={1} />}

        {showAdminBlock && sync.error && !isAdminGated && (
          <InlineError error={sync.error} />
        )}

        {showAdminBlock && sync.data && (
          stale.length === 0 ? (
            <div className="flex items-center gap-2 rounded-md border bg-emerald-50 p-2.5 text-sm text-emerald-700">
              <CheckCircle2 className="h-4 w-4" />
              Ordio-Sync aktuell ({sync.data.companies.length}{" "}
              {sync.data.companies.length === 1 ? "Unternehmen" : "Unternehmen"}).
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-700">
                <AlertTriangle className="h-4 w-4" />
                {stale.length === 1
                  ? "1 Unternehmen veraltet"
                  : `${stale.length} Unternehmen veraltet`}
              </div>
              <ul className="space-y-1">
                {stale.slice(0, 4).map((c) => (
                  <li
                    key={c.companyId}
                    className="flex items-center justify-between rounded-md border bg-background px-2.5 py-1.5 text-xs"
                  >
                    <span className="truncate">{c.companyName}</span>
                    <span className="text-muted-foreground">
                      {formatRelative(c.lastBaseDataSyncedAt) ?? "—"}
                    </span>
                  </li>
                ))}
              </ul>
              <Link
                to="/o/$slug/sync-status"
                params={{ slug }}
                className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                Sync-Status öffnen
              </Link>
            </div>
          )
        )}

        {!companiesLoading &&
          driveDisconnected.count === 0 &&
          isAdminGated && (
            <p className="text-sm text-muted-foreground">Alles ruhig.</p>
          )}
      </CardContent>
    </Card>
  );
}

// ── Fan-out signal hook ───────────────────────────────────────────────────

function useDashboardSignals(slug: string, companies: CompanyDto[]): DashboardSignals {
  // Per-company queries. Each carries a 5-min staleTime so dashboard revisits
  // and a focus-refetch don't thrash N×4 endpoints. retry:false because a
  // single 403 (e.g. Drive endpoint without Manager role) shouldn't poison the
  // aggregate — we just skip its contribution.
  const docs = useQueries({
    queries: companies.map((c) => ({
      queryKey: ["documents", c.id, "needs-review"] as const,
      queryFn: () =>
        apiFetch<DocumentSummaryDto[]>(`/api/companies/${c.id}/documents?needsReview=true`),
      staleTime: 5 * 60_000,
      retry: false,
    })),
  });

  const contracts = useQueries({
    queries: companies.map((c) => ({
      queryKey: ["contracts", c.id] as const,
      queryFn: () => apiFetch<ContractSummaryDto[]>(`/api/contracts?companyId=${c.id}`),
      staleTime: 5 * 60_000,
      retry: false,
    })),
  });

  const employees = useQueries({
    queries: companies.map((c) => ({
      queryKey: ["employees", slug, c.id] as const,
      queryFn: () =>
        apiFetch<EmployeeDirectoryRowDto[]>(`/api/o/${slug}/companies/${c.id}/employees`),
      staleTime: 5 * 60_000,
      retry: false,
    })),
  });

  const drive = useQueries({
    queries: companies.map((c) => ({
      queryKey: ["drive", "status", c.id] as const,
      queryFn: () => apiFetch<DriveConnectionStatusDto>(`/api/companies/${c.id}/drive`),
      staleTime: 5 * 60_000,
      retry: false,
    })),
  });

  const isLoading =
    companies.length > 0 &&
    [docs, contracts, employees, drive].some((qs) => qs.some((q) => q.isLoading));

  // Aggregate. `firstCompanyId` is the first company that contributes a
  // positive count — used as the link target where there's no org-level
  // landing page yet (documents).
  const documentsToReview = aggregate(companies, docs, (rows) => rows.length);
  const contractsSendFailed = aggregate(companies, contracts, (rows) =>
    rows.filter((r) => r.status === ContractStatus.SendFailed).length,
  );
  const contractDrafts = aggregate(companies, contracts, (rows) =>
    rows.filter((r) => r.status === ContractStatus.Draft).length,
  );
  const orphanEmployees = aggregate(companies, employees, (rows) =>
    rows.filter((r) => classifyEmployee(r) === "orphan-wages").length,
  );
  const driveDisconnected = aggregate(companies, drive, (row) => (row.connected ? 0 : 1));

  const hasAnyError = [docs, contracts, employees, drive].some((qs) => qs.some((q) => q.error));

  return {
    documentsToReview,
    contractsSendFailed,
    contractDrafts,
    orphanEmployees,
    driveDisconnected,
    isLoading,
    hasAnyError,
  };
}

interface LiveEntries {
  entries: TimeClockEntryDto[];
  companyCount: number;
  isLoading: boolean;
  isFetching: boolean;
  hasErrors: boolean;
}

function useLiveEntries(slug: string, companies: CompanyDto[]): LiveEntries {
  // One /live call per company. The endpoint paginates via search-shifts under
  // the hood, so the cost is bounded per company (not per employee). 30 s
  // refetch keeps the panel feeling live without burning the Ordio key budget.
  const results = useQueries({
    queries: companies.map((c) => ({
      queryKey: ["live", slug, c.id] as const,
      queryFn: () => apiFetch<TimeClockResponseDto>(`/api/o/${slug}/companies/${c.id}/live`),
      staleTime: 20_000,
      refetchInterval: 30_000,
      refetchIntervalInBackground: false,
      retry: false,
    })),
  });

  const entries: TimeClockEntryDto[] = [];
  let hasErrors = false;
  for (const r of results) {
    if (r.error) hasErrors = true;
    if (!r.data) continue;
    if (r.data.failedCompanyNames.length > 0) hasErrors = true;
    entries.push(...r.data.live);
  }
  entries.sort((a, b) => {
    const ai = a.clockedInAt ?? a.shiftStartsAt;
    const bi = b.clockedInAt ?? b.shiftStartsAt;
    return +new Date(ai) - +new Date(bi);
  });

  return {
    entries,
    companyCount: companies.length,
    isLoading: companies.length > 0 && results.some((r) => r.isLoading),
    isFetching: results.some((r) => r.isFetching),
    hasErrors,
  };
}

function aggregate<T>(
  companies: CompanyDto[],
  results: UseQueryResult<T, Error>[],
  countFor: (data: T) => number,
): { count: number; firstCompanyId: string | null } {
  let total = 0;
  let first: string | null = null;
  for (let i = 0; i < results.length; i++) {
    const r = results[i];
    if (!r.data) continue;
    const n = countFor(r.data);
    if (n > 0 && first == null) first = companies[i]?.id ?? null;
    total += n;
  }
  return { count: total, firstCompanyId: first };
}

// ── Helpers ───────────────────────────────────────────────────────────────

/**
 * Combine the org-wide event list (everyone's visible events) with the
 * personal /me/events stream (which adds Ordio shifts + absences). The
 * personal stream also includes javeed-event rows when the user is an
 * attendee — those would duplicate org-event rows, so we drop them.
 */
function mergeAgendaSources(
  orgEvents: CalendarEventDto[] | undefined,
  personal: MyCalendarItemDto[] | undefined,
): MyCalendarItemDto[] {
  const out: MyCalendarItemDto[] = [];
  if (orgEvents) {
    for (const e of orgEvents) {
      out.push({
        kind: "javeed-event",
        startsAt: e.startsAt,
        endsAt: e.endsAt,
        title: e.title,
        eventId: e.id,
        ordioIdentityId: null,
        companyId: null,
      });
    }
  }
  if (personal) {
    for (const p of personal) {
      if (p.kind === "javeed-event") continue;
      out.push(p);
    }
  }
  out.sort((a, b) => +new Date(a.startsAt) - +new Date(b.startsAt));
  return out;
}

function splitAgenda(items: MyCalendarItemDto[]): {
  today: MyCalendarItemDto[];
  week: MyCalendarItemDto[];
} {
  const now = Date.now();
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const endOfToday = new Date(startOfToday);
  endOfToday.setDate(endOfToday.getDate() + 1);
  const today: MyCalendarItemDto[] = [];
  const week: MyCalendarItemDto[] = [];
  for (const item of items) {
    const start = new Date(item.startsAt).getTime();
    const end = new Date(item.endsAt).getTime();
    if (start < endOfToday.getTime()) {
      // Drop items that already ended — at 18:00 we don't want to see the
      // 09:00 standup in the "Heute" list. Ongoing items (end > now) stay.
      if (end > now) today.push(item);
    } else {
      week.push(item);
    }
  }
  return { today, week };
}

/**
 * Window covering today 00:00 through the end of the current calendar week
 * (Monday-first, German convention). On Monday this is ~7 days; on Sunday it
 * collapses to today only. Matches the "Diese Woche / bis Wochenende" label.
 */
function useDashboardWindow() {
  const now = new Date();
  const from = new Date(now);
  from.setHours(0, 0, 0, 0);
  const nextMonday = new Date(startOfWeek(now));
  nextMonday.setDate(nextMonday.getDate() + 7);
  return { fromIso: from.toISOString(), toIso: nextMonday.toISOString() };
}

function ListSkeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-14" />
      ))}
    </div>
  );
}

function InlineError({ error }: { error: Error }) {
  const status = error instanceof ApiError ? error.status : null;
  return (
    <div className="flex items-start gap-2 text-sm text-destructive">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
        {status === 401
          ? "Nicht angemeldet."
          : status === 403
            ? "Keine Berechtigung."
            : error.message}
      </span>
    </div>
  );
}
